import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useOrderQuotationProductInputQuery } from '../../../../../../../../../services';
import { RhSingleAutocomplete } from '../../../../../../../../dum';
import { IOrderQuotation } from '../../../order-quotation-form';
import { useCalculateProductsTotal } from '../../../helpers/use-calculate-products-total';
import {
    GROUP_WEIGHT,
    ORDER_QUOTATION_PRODUCTS,
    PRODUCT,
} from '../order-quotation-products-columns';
import { FIELD_LABELS, shortLabel } from '../../../../../../../../../helpers';

export function OrderQuotationProductInput({ index }: { index: number }) {
    // Either/or: pick an existing product OR clear it and type a
    // proposed_description (free line). The picker spans ALL products, not the
    // account catalog — a cotización may quote a product the client does not yet
    // have. Prices are NOT filled from a catalog (free-priced by design); only
    // group_weight follows the product, since the product owns it.
    const { data } = useOrderQuotationProductInputQuery();

    const options = useMemo(() => {
        return (data?.getProducts || []).map((product) => ({
            id: product.id,
            compound_description: product.compound_description,
            current_group_weight: product.current_group_weight,
        }));
    }, [data]);

    const INPUT_NAME = `${ORDER_QUOTATION_PRODUCTS}.${index}.${PRODUCT}`;
    const orderQuotationProductRow = useWatch({
        name: `${ORDER_QUOTATION_PRODUCTS}.${index}`,
    }) as IOrderQuotation['order_quotation_products'][number];

    const formContext = useFormContext();

    const { calculateProductsTotal } = useCalculateProductsTotal();

    return (
        <RhSingleAutocomplete
            itemText={'compound_description'}
            itemValue={'id'}
            returnObject
            options={options}
            name={INPUT_NAME}
            label={shortLabel(FIELD_LABELS.product_id)}
            disableGutters
            disabled={!!orderQuotationProductRow.id}
            onChange={(product) => {
                if (!!product && !orderQuotationProductRow.id) {
                    const catalogProduct = options.find(
                        (p) => p.id === product.id,
                    );

                    if (catalogProduct) {
                        // Only group_weight follows the product; prices stay
                        // whatever the user typed (free line rules apply to
                        // linked lines too — no catalog price).
                        formContext.setValue(
                            `${ORDER_QUOTATION_PRODUCTS}.${index}.${GROUP_WEIGHT}`,
                            catalogProduct.current_group_weight || 0,
                        );
                    }
                    calculateProductsTotal();
                }
            }}
        />
    );
}
